import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { TrophyIcon, SparklesIcon } from "@heroicons/react/24/solid"
import PlayerListItemInfo from "@/Pages/Game/PlayerListItemInfo"
import { useNavigate } from "react-router-dom"
import { getUserCreds, socket } from "@/utils/utils"
import { GameEndModalCTA } from "@/Modals/GameEndModalCTA"
import Modal from "@/Components/Modal"
import GameStatsTable from "@/Components/GameStatsTable"

const GameEndModalWithoutStats: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const navigate = useNavigate()
  const modalData = useAppSelector((state) => state.global.modalData)

  const handleClose = () => {
    dispatch(setModalData({ gameEndWithoutStats: { visible: false } }))
  }

  const handleGoToLobby = () => {
    handleClose()
    const thisPlayer = myGame?.situation?.players.find(
      (item) => item.id === getUserCreds().email
    )
    socket.emit("game.leave-game", myGame?.id || "", thisPlayer!.order)
    navigate("/lobby")
  }

  if (!modalData.gameEndWithoutStats?.visible) return null

  const situationPlayers = myGame?.situation?.players
  const winnerPlayer = situationPlayers?.find((p) => !p.isBankrupt)
  const winnerUser = myGame?.players.find((p) => p.email === winnerPlayer?.id)

  // Winner on top, everyone else in seating order.
  const rows = [...(situationPlayers || [])].sort((a, b) =>
    a.order === winnerPlayer?.order
      ? -1
      : b.order === winnerPlayer?.order
      ? 1
      : a.order - b.order
  )

  return (
    <Modal
      data-testid="modal.game-end-without-stats"
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[97%] max-w-[100rem] max-h-[92vh] z-[100] flex flex-col"
      onClose={handleClose}
      icon={<TrophyIcon className="h-8 w-8 text-white" />}
      title="Game Over!"
      subtitle="We have a winner"
      headerClassName="pt-6 pb-4"
      contentClassName="flex-1 min-h-0 overflow-hidden px-6 pb-6 flex flex-col"
    >
      {winnerUser && winnerPlayer && (
        <div className="relative">
          {/* Ambient gold glow radiating behind the champion bar */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-4 rounded-full bg-amber-500/20 blur-3xl"
          />

          {/* Champion bar — gold gradient border wrapping a dark inner panel */}
          <div className="relative rounded-2xl bg-gradient-to-br from-amber-200 via-yellow-500 to-amber-600 p-[2px] shadow-xl shadow-amber-500/20">
            <div className="relative flex items-center gap-4 overflow-hidden rounded-[15px] bg-gradient-to-b from-gray-900 via-gray-900 to-black px-4 py-2">
              {/* Soft radial highlight spilling from the top */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 -top-16 h-32 bg-amber-400/20 blur-3xl"
              />

              {/* WINNER eyebrow with gold gradient text and sparkles */}
              <div className="relative flex shrink-0 items-center gap-2">
                <SparklesIcon className="h-4 w-4 text-amber-300/80" />
                <span className="bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-200 bg-clip-text font-poppins text-base font-bold uppercase tracking-[0.3em] text-transparent">
                  Winner
                </span>
                <SparklesIcon className="h-4 w-4 text-amber-300/80" />
              </div>

              {/* The winner's nameplate, framed by a subtle gold ring */}
              <div className="relative min-w-0 flex-1 overflow-hidden rounded-xl shadow-inner ring-1 ring-amber-400/30">
                <PlayerListItemInfo
                  player={winnerPlayer}
                  user={winnerUser}
                  distinguishActivePlayer={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <GameStatsTable
        className="mt-4 flex-1 min-h-0"
        players={rows}
        users={myGame?.players}
        winnerOrder={winnerPlayer?.order}
        historyItems={myGame?.situation?.historyItems}
        completedTrades={myGame?.situation?.completedTrades}
      />

      <GameEndModalCTA onGoToLobby={handleGoToLobby} />
    </Modal>
  )
}

export default GameEndModalWithoutStats
